import React, {
  memo,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import API from "Utils/api";
import { getCurrentEnvironment } from "Utils/constants";
import { otaComponentsApi, otaVersionsApi, otaVehiclesApi } from "../../api";
import {
  OTAComponent,
  OTAComponentCreatePayload,
  OTAComponentVersion,
  OTAShadow,
  OTAVehicle,
  SyncStatus,
} from "../../types";
import { SyncStatusBadge } from "../OTADataView/styles";
import {
  parseReleaseVersionTag,
  formatComponentVersion,
} from "../../utils/versionEncoding";

interface ReleaseComponent {
  id: string;
  componentName: string;
  awsComponentName: string;
  versionNumber: string;
  componentType: string;
}

// Build a flat map of componentName → installed version from the vehicle's
// device shadow (VehicleTelemetry >= 1.0.7 format only).
// Containers are written first so that firmware keys always win on collision.
export function buildShadowVersionMap(
  shadow: OTAShadow | null
): Record<string, string> {
  if (!shadow) return {};
  const map: Record<string, string> = {};

  // Flat container versions: { "autonomy-stack": "1.5.0" }
  if (shadow.containers) {
    for (const [k, v] of Object.entries(shadow.containers)) {
      if (typeof v === "string") map[k] = v;
    }
  }

  // Flat firmware versions: { "stw": "2.3.7.3", "esp32": "0.2.4.0" }
  // Guard against the brief 1.0.7-transition nested-object format.
  if (shadow.firmware) {
    for (const [k, v] of Object.entries(shadow.firmware)) {
      if (typeof v === "string") map[k] = v;
      else if (v && typeof v === "object") {
        const c = (v as Record<string, unknown>).current;
        if (typeof c === "string") map[k] = c;
      }
    }
  }

  return map;
}

import VehicleTelemetryPanel from "../VehicleTelemetryPanel";
import ShadowStatusPanel from "../ShadowStatusPanel";
import { CreateFleetModal } from "../CreateFleetModal";
import { AddVehicleTypeModal } from "../AddVehicleTypeModal";
import { AddComponentModal } from "../AddComponentModal";
import { AddVersionModal } from "../AddVersionModal";
import { DeleteConfirmationModal } from "../DeleteConfirmationModal";
import { OTAVehicleAssignModal } from "../OTAVehicleAssignModal";
import { VersionDetailsModal } from "../VersionDetailsModal";
import { otaLogger } from "../../logger";

const log = otaLogger.byPrefix("OTAOverviewCards");

// ─── Local types ─────────────────────────────────────────────────────────────

interface CardFleet {
  id: string;
  name: string;
  description: string;
  awsThingGroupName: string | null;
  isActive: boolean;
  vehicleCount: number;
  deploymentSessionCount: number;
  legacyFleetName: string | null;
  syncStatus: SyncStatus;
  existsInGg: boolean;
  lastGgSyncAt: string | null;
}

export interface CardVehicle {
  id: string;
  vin: string | null;
  legacyVehicleName: string | null;
  vehicleType: string | null;
  vehicleTypeName: string;
  fleetName: string | null;
  fleet: string | null;
  awsThingName: string;
  connectionStatus: string;
  telemetryLastSeen: string | null;
  environment: string;
  currentReleaseName: string | null;
}

interface CardVehicleType {
  id: string;
  name: string;
  description: string;
  isDeprecated: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function statusColor(status: string): string {
  switch (status) {
    case "online":
      return "#22c55e";
    case "offline":
      return "#6b7280";
    case "provisioning":
    case "disconnected":
      return "#f59e0b";
    case "error":
      return "#ef4444";
    default:
      return "#9ca3af";
  }
}

export function formatDate(iso: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

// ─── Styled components ────────────────────────────────────────────────────────

const BlockWrapper = styled.div`
  margin-bottom: 32px;
`;

const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const BlockTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #172a3f;
  margin: 0;
`;

const BlockCount = styled.span`
  font-size: 13px;
  color: #8a9ab0;
  flex: 1;
`;

const CreateBtn = styled.button`
  font-size: 12px;
  font-weight: 600;
  color: #55b8b3;
  background: #f0fafa;
  border: 1px solid #b2e3e1;
  border-radius: 8px;
  padding: 5px 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: #55b8b3;
    color: #ffffff;
    border-color: #55b8b3;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 18px 20px;
  text-align: left;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease,
    transform 0.1s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(23, 42, 63, 0.1);
    border-color: #55b8b3;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CardName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #172a3f;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 6px;
  flex-shrink: 0;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
  font-size: 12px;
  color: #8a9ab0;
`;

const VehicleCountBadge = styled.span`
  background: #f0fafa;
  color: #55b8b3;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  padding: 2px 8px;
  margin-left: auto;
`;

const DeprecatedBadge = styled.span`
  background: #fff7ed;
  color: #c2410c;
  font-size: 10px;
  font-weight: 600;
  border-radius: 10px;
  padding: 2px 8px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const EmptyBlock = styled.div`
  color: #9ca3af;
  font-size: 14px;
  font-style: italic;
  padding: 8px 0;
`;

// ─── Filter ───────────────────────────────────────────────────────────────────

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  font-size: 13px;
  color: #172a3f;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 32px 6px 12px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2364748b'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: #55b8b3;
  }
  &:hover {
    border-color: #cbd5e1;
  }
`;

// ─── Modal shared ─────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ModalBox = styled.div`
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 1;
  gap: 12px;
`;

const ModalTitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const ModalTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: #172a3f;
  margin: 0;
`;

const ModalSubtitle = styled.div`
  font-size: 12px;
  color: #8a9ab0;
  margin-top: 2px;
`;

const ModalHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const PopupActionBtn = styled.button<{ $variant: "warn" | "danger" }>`
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 7px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;

  ${({ $variant }) =>
    $variant === "danger"
      ? `
    color: #dc2626;
    background: #fee2e2;
    border-color: #fca5a5;
    &:hover { background: #dc2626; color: #fff; border-color: #dc2626; }
  `
      : `
    color: #d97706;
    background: #fef3c7;
    border-color: #fcd34d;
    &:hover { background: #d97706; color: #fff; border-color: #d97706; }
  `}
`;

const CloseButton = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: #e2e8f0;
    color: #172a3f;
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px 24px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 10px;
  margin-top: 20px;
  &:first-child {
    margin-top: 0;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 4px;
`;

const InfoItem = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 10px 12px;
`;

const InfoLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 3px;
`;

const InfoValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #172a3f;
  word-break: break-all;
`;

const VehicleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  margin-bottom: 8px;
`;

const VehicleRowLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #172a3f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const VehicleRowMeta = styled.div`
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const VehicleRowRelease = styled.div`
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ComponentRow = styled.div`
  display: flex;
  align-items: center;
  padding: 9px 12px;
  border-radius: 8px;
  background: #f8fafc;
  margin-bottom: 6px;
  gap: 8px;
`;

const ComponentName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #172a3f;
  flex: 1;
`;

const ComponentType = styled.div`
  font-size: 11px;
  color: #8a9ab0;
  background: #e8f0fe;
  padding: 2px 7px;
  border-radius: 10px;
  flex-shrink: 0;
`;

const InstalledVersion = styled.div`
  font-size: 11px;
  color: #22c55e;
  font-family: monospace;
  background: #052e16;
  padding: 2px 7px;
  border-radius: 10px;
  flex-shrink: 0;
`;

const VersionNumber = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #172a3f;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  min-width: 52px;
  transition: color 0.15s;
`;

const VersionRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
  margin-bottom: 6px;
  gap: 8px;

  &:hover ${VersionNumber} {
    color: #0369a1;
    text-decoration: underline;
  }
`;

const VersionMeta = styled.div`
  font-size: 11px;
  color: #94a3b8;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VersionActions = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`;

const SmallActionBtn = styled.button<{ $variant: "warn" | "danger" }>`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  line-height: 1.4;

  ${({ $variant }) =>
    $variant === "danger"
      ? `
    color: #dc2626; background: #fee2e2; border-color: #fca5a5;
    &:hover { background: #dc2626; color: #fff; border-color: #dc2626; }
  `
      : `
    color: #d97706; background: #fef3c7; border-color: #fcd34d;
    &:hover { background: #d97706; color: #fff; border-color: #d97706; }
  `}
`;

const LoadingText = styled.div`
  font-size: 13px;
  color: #9ca3af;
  padding: 12px 0;
`;

const AddVersionBtn = styled.button`
  font-size: 13px;
  font-weight: 600;
  color: #55b8b3;
  background: transparent;
  border: 1px solid #55b8b3;
  border-radius: 6px;
  padding: 6px 14px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:hover {
    background: #55b8b3;
    color: #ffffff;
  }
`;

const DeleteFleetBtn = styled.button<{ enabled: boolean }>`
  height: 36px;
  padding: 0 20px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: ${({ enabled }) => (enabled ? "pointer" : "not-allowed")};
  background: ${({ enabled }) => (enabled ? "#dc2626" : "#f9a8a8")};
  color: #ffffff;
  border: none;
  border-radius: 4px;
  opacity: ${({ enabled }) => (enabled ? 1 : 0.5)};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const CancelBtn = styled.button`
  height: 36px;
  padding: 0 20px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  background: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  &:hover {
    background: #f9fafb;
  }
`;

// ─── Fleet Modal ──────────────────────────────────────────────────────────────

const FleetModal = memo<{
  fleet: CardFleet;
  vehicles: CardVehicle[];
  onClose: () => void;
}>(({ fleet, vehicles, onClose }) => {
  const isActive = fleet.isActive;
  const fleetVehicles = vehicles.filter((v) => v.fleet === fleet.id);

  return (
    <>
      <Overlay onClick={onClose}>
        <ModalBox onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitleBlock>
              <ModalTitle>{fleet.name}</ModalTitle>
              <ModalSubtitle>
                {fleet.description || "No description"} ·{" "}
                {isActive ? "Active" : "Inactive"}
              </ModalSubtitle>
            </ModalTitleBlock>
            <CloseButton onClick={onClose}>×</CloseButton>
          </ModalHeader>
          <ModalBody>
            {fleet.awsThingGroupName && (
              <>
                <SectionLabel>AWS Thing Group</SectionLabel>
                <InfoGrid>
                  <InfoItem style={{ gridColumn: "1 / -1" }}>
                    <InfoLabel>Thing Group Name</InfoLabel>
                    <InfoValue>{fleet.awsThingGroupName}</InfoValue>
                  </InfoItem>
                </InfoGrid>
              </>
            )}
            <SectionLabel>Legacy FMS Fleet</SectionLabel>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Sync Status</InfoLabel>
                <InfoValue>
                  <SyncStatusBadge
                    status={fleet.syncStatus}
                    stale={fleet.existsInGg === false}
                  >
                    {fleet.existsInGg === false
                      ? "Not in GG"
                      : fleet.syncStatus === "synced"
                      ? "Synced"
                      : fleet.syncStatus === "legacy_only"
                      ? "Legacy Only"
                      : "GG Only"}
                  </SyncStatusBadge>
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>FMS Fleet</InfoLabel>
                <InfoValue>
                  {fleet.legacyFleetName ?? (
                    <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                      Not linked to FMS
                    </span>
                  )}
                </InfoValue>
              </InfoItem>
              {fleet.lastGgSyncAt && (
                <InfoItem>
                  <InfoLabel>Last GG Sync</InfoLabel>
                  <InfoValue>{formatDate(fleet.lastGgSyncAt)}</InfoValue>
                </InfoItem>
              )}
            </InfoGrid>
            <SectionLabel>Vehicles ({fleetVehicles.length})</SectionLabel>
            {fleetVehicles.length === 0 ? (
              <EmptyBlock>No vehicles assigned to this fleet</EmptyBlock>
            ) : (
              fleetVehicles.map((v) => (
                <VehicleRow key={v.id}>
                  <div style={{ minWidth: 0, flex: 1, marginRight: 12 }}>
                    <VehicleRowLabel
                      title={v.awsThingName || v.legacyVehicleName || undefined}
                    >
                      {v.awsThingName || v.legacyVehicleName || (
                        <em style={{ color: "#9ca3af" }}>Unknown</em>
                      )}
                    </VehicleRowLabel>
                    <VehicleRowMeta>{v.vehicleTypeName}</VehicleRowMeta>
                    <VehicleRowRelease
                      title={v.currentReleaseName ?? undefined}
                    >
                      {v.currentReleaseName ?? "No release assigned"}
                    </VehicleRowRelease>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexShrink: 0,
                    }}
                  >
                    <StatusDot $color={statusColor(v.connectionStatus)} />
                    <span style={{ fontSize: 12, color: "#64748b" }}>
                      {v.connectionStatus}
                    </span>
                  </div>
                </VehicleRow>
              ))
            )}
          </ModalBody>
        </ModalBox>
      </Overlay>
    </>
  );
});

// ─── Vehicle Modal ────────────────────────────────────────────────────────────

export const VehicleModal = memo<{
  vehicle: CardVehicle;
  onClose: () => void;
  onDataChanged: () => void;
}>(({ vehicle, onClose, onDataChanged }) => {
  const [details, setDetails] = useState<OTAVehicle | null>(null);
  const [releaseComponents, setReleaseComponents] = useState<
    ReleaseComponent[]
  >([]);
  // The release's own ccm_autonomy (e.g. "11.4.m3") -- the reliable source
  // for the rc/master tag. The release's display `name` is free-form user
  // input (ReleaseWizard has no validation tying it to ccm_autonomy) so it
  // can't be trusted alone; kept only as a fallback for the yaml-generated
  // case where the name does happen to embed the tag (PR #1140 review).
  const [releaseCcmAutonomy, setReleaseCcmAutonomy] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [thingAttributes, setThingAttributes] = useState<
    Record<string, string>
  >({});

  // Refetch vehicle details without resetting the loading spinner — called
  // after the shadow endpoint reconciles current_release from session_id.
  const refetchVehicleDetails = useCallback(() => {
    API.get(`/ota/vehicles/${vehicle.id}/`)
      .then((res) => {
        const d = res.data;
        setDetails((prev) => {
          if (!prev) return prev;
          const resolvedRelease = d.currentRelease
            ? typeof d.currentRelease === "object"
              ? String(d.currentRelease.id)
              : d.currentRelease
            : null;
          const resolvedReleaseName =
            d.currentReleaseName ??
            (d.currentRelease !== null && typeof d.currentRelease === "object"
              ? d.currentRelease.name
              : null) ??
            null;
          const updated = {
            ...prev,
            currentRelease: resolvedRelease ?? prev.currentRelease,
            currentReleaseName: resolvedReleaseName ?? prev.currentReleaseName,
          };
          if (
            updated.currentRelease &&
            updated.currentRelease !== prev.currentRelease
          ) {
            API.get(`/ota/releases/${updated.currentRelease}/`)
              .then((r) => {
                setReleaseComponents(r.data?.components ?? []);
                setReleaseCcmAutonomy(r.data?.ccmAutonomy ?? null);
              })
              .catch(() => undefined);
          }
          return updated;
        });
      })
      .catch(() => undefined);
  }, [vehicle.id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    API.get(`/ota/vehicles/${vehicle.id}/`)
      .then((res) => {
        if (cancelled) return;
        const d = res.data;
        // Detail endpoint returns vehicle_type and fleet as nested objects.
        // Extract the UUID and name from nested objects, falling back to
        // the flat string fields if somehow the data is shaped differently.
        const vtObj = d.vehicleType;
        const vtId: string =
          (vtObj && typeof vtObj === "object"
            ? String(vtObj.id)
            : vtObj ?? "") ||
          vehicle.vehicleType ||
          "";
        const vtName: string =
          d.vehicleTypeName ??
          (vtObj && typeof vtObj === "object" ? vtObj.name : null) ??
          vehicle.vehicleTypeName ??
          "";
        const fleetObj = d.fleet;
        const fleetId: string | null =
          fleetObj && typeof fleetObj === "object"
            ? String(fleetObj.id)
            : fleetObj ?? null;
        const fleetName: string | null =
          d.fleetName ??
          (fleetObj && typeof fleetObj === "object" ? fleetObj.name : null) ??
          null;

        const mapped: OTAVehicle = {
          id: d.id,
          vin: d.vin,
          vehicleType: vtObj,
          vehicleTypeId: vtId,
          vehicleTypeName: vtName,
          fleet: fleetObj,
          fleetId: fleetId,
          fleetName: fleetName,
          currentRelease: d.currentRelease
            ? typeof d.currentRelease === "object"
              ? String(d.currentRelease.id)
              : d.currentRelease
            : null,
          currentReleaseName:
            d.currentReleaseName ??
            (d.currentRelease && typeof d.currentRelease === "object"
              ? d.currentRelease.name
              : null) ??
            null,
          awsThingName: d.awsThingName ?? "",
          awsThingArn: d.awsThingArn ?? null,
          awsCertificateId: d.awsCertificateId ?? null,
          awsCertificateArn: d.awsCertificateArn ?? null,
          awsPolicyName: d.awsPolicyName ?? null,
          awsGreengrassCoreDeviceName: d.awsGreengrassCoreDeviceName ?? null,
          awsGreengrassCoreDeviceArn: d.awsGreengrassCoreDeviceArn ?? null,
          connectionStatus: d.connectionStatus ?? "unknown",
          lastConnectedAt: d.lastConnectedAt ?? null,
          baselineDeploymentId: d.baselineDeploymentId ?? "",
          telemetryLastSeen: d.telemetryLastSeen ?? null,
          telemetryData: d.telemetryData ?? null,
          notes: d.notes ?? "",
          isActive: d.isActive ?? true,
          legacyVehicleId: d.legacyVehicleId ?? null,
          legacyVehicleName: d.legacyVehicleName ?? null,
          syncStatus: d.syncStatus ?? "gg_only",
          existsInGg: d.existsInGg ?? true,
          lastGgSyncAt: d.lastGgSyncAt ?? null,
          environment: d.environment ?? "",
          createdAt: d.createdAt ?? "",
          updatedAt: d.updatedAt ?? "",
        };
        setDetails(mapped);
        if (mapped.currentRelease) {
          API.get(`/ota/releases/${mapped.currentRelease}/`)
            .then((r) => {
              if (!cancelled) {
                setReleaseComponents(r.data?.components ?? []);
                setReleaseCcmAutonomy(r.data?.ccmAutonomy ?? null);
              }
            })
            .catch(() => undefined);
        }
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load vehicle details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Fetch Thing attributes in parallel — live from AWS IoT
    otaVehiclesApi
      .getThingAttributes(vehicle.id)
      .then((attrs) => {
        if (!cancelled) setThingAttributes(attrs);
      })
      .catch(() => {
        /* non-fatal */
      });

    return () => {
      cancelled = true;
    };
  }, [vehicle.id]);

  // Computed once per release, not per component -- see versionEncoding.ts
  // for why decoding must be anchored to the release's own tag rather than
  // guessing from each component's version number in isolation.
  // ccm_autonomy is the reliable source for the tag; the release's free-form
  // display name is only a fallback, for the yaml-generated case where the
  // name happens to embed it too (PR #1140 review).
  const releaseTag = useMemo(
    () =>
      parseReleaseVersionTag(releaseCcmAutonomy) ??
      parseReleaseVersionTag(details?.currentReleaseName),
    [releaseCcmAutonomy, details?.currentReleaseName]
  );

  return (
    <>
      <Overlay onClick={onClose}>
        <ModalBox onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitleBlock>
              <ModalTitle>
                {vehicle.awsThingName || vehicle.vin || (
                  <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                    No name
                  </span>
                )}
              </ModalTitle>
              <ModalSubtitle>
                {vehicle.vehicleTypeName || "No type"} ·{" "}
                {vehicle.fleetName ?? "No fleet"}
              </ModalSubtitle>
            </ModalTitleBlock>
            <ModalHeaderActions>
              <PopupActionBtn
                $variant="warn"
                onClick={() => setShowAssign(true)}
                style={{
                  color: "#55b8b3",
                  background: "#f0fafa",
                  borderColor: "#b2e3e1",
                }}
              >
                {vehicle.vehicleType && vehicle.fleet
                  ? "Change Assignment"
                  : "Assign Type & Fleet"}
              </PopupActionBtn>
            </ModalHeaderActions>
            <CloseButton onClick={onClose}>×</CloseButton>
          </ModalHeader>
          <ModalBody>
            {loading ? (
              <LoadingText>Loading vehicle details…</LoadingText>
            ) : details ? (
              <>
                <SectionLabel>General</SectionLabel>
                <InfoGrid>
                  <InfoItem style={{ gridColumn: "1 / -1" }}>
                    <InfoLabel>Vehicle ID</InfoLabel>
                    <InfoValue
                      style={{ fontFamily: "monospace", fontSize: 12 }}
                    >
                      {details.id}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>AWS Thing Name</InfoLabel>
                    <InfoValue
                      title={
                        details.awsThingName ||
                        vehicle.awsThingName ||
                        undefined
                      }
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        wordBreak: "normal",
                      }}
                    >
                      {details.awsThingName || vehicle.awsThingName || "—"}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>VIN</InfoLabel>
                    <InfoValue style={{ fontFamily: "monospace" }}>
                      {details.vin || vehicle.vin || (
                        <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                          Not yet reported
                        </span>
                      )}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Connection</InfoLabel>
                    <InfoValue>
                      <StatusDot
                        $color={statusColor(details.connectionStatus)}
                      />
                      {details.connectionStatus}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Vehicle Type</InfoLabel>
                    <InfoValue>{details.vehicleTypeName || "—"}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Fleet</InfoLabel>
                    <InfoValue>{details.fleetName ?? "—"}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Current Release</InfoLabel>
                    <InfoValue>{details.currentReleaseName ?? "—"}</InfoValue>
                  </InfoItem>
                </InfoGrid>
                <SectionLabel>Legacy FMS Vehicle</SectionLabel>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>Sync Status</InfoLabel>
                    <InfoValue>
                      <SyncStatusBadge
                        status={details.syncStatus}
                        stale={details.existsInGg === false}
                      >
                        {details.existsInGg === false
                          ? "Not in GG"
                          : details.syncStatus === "synced"
                          ? "Synced"
                          : details.syncStatus === "legacy_only"
                          ? "Legacy Only"
                          : "GG Only"}
                      </SyncStatusBadge>
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>FMS Vehicle</InfoLabel>
                    <InfoValue>
                      {details.legacyVehicleName ?? (
                        <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                          Not linked to FMS
                        </span>
                      )}
                    </InfoValue>
                  </InfoItem>
                  {details.lastGgSyncAt && (
                    <InfoItem>
                      <InfoLabel>Last GG Sync</InfoLabel>
                      <InfoValue>{formatDate(details.lastGgSyncAt)}</InfoValue>
                    </InfoItem>
                  )}
                </InfoGrid>
                {Object.keys(thingAttributes).length > 0 && (
                  <>
                    <SectionLabel>Thing Attributes</SectionLabel>
                    <InfoGrid>
                      {Object.entries(thingAttributes).map(([key, value]) => (
                        <InfoItem key={key}>
                          <InfoLabel
                            style={{ fontFamily: "monospace", fontSize: 11 }}
                          >
                            {key}
                          </InfoLabel>
                          <InfoValue
                            style={{ fontFamily: "monospace", fontSize: 12 }}
                          >
                            {value || "—"}
                          </InfoValue>
                        </InfoItem>
                      ))}
                    </InfoGrid>
                  </>
                )}
                <SectionLabel>Telemetry</SectionLabel>
                <VehicleTelemetryPanel vehicle={details} />
                <SectionLabel>OTA Shadow</SectionLabel>
                <ShadowStatusPanel
                  vehicle={details}
                  onShadowLoaded={refetchVehicleDetails}
                />
                <SectionLabel>
                  Installed Components ({releaseComponents.length})
                </SectionLabel>
                {releaseComponents.length === 0 ? (
                  <EmptyBlock>
                    {details?.currentRelease
                      ? "Loading components…"
                      : "No release deployed on this vehicle"}
                  </EmptyBlock>
                ) : (
                  releaseComponents.map((c) => {
                    const { display, actual } = formatComponentVersion(
                      c.versionNumber,
                      releaseTag
                    );
                    return (
                      <ComponentRow key={c.id}>
                        <ComponentName>{c.componentName}</ComponentName>
                        {c.componentType && (
                          <ComponentType>{c.componentType}</ComponentType>
                        )}
                        <InstalledVersion
                          title={
                            actual
                              ? `Actual assigned version: ${actual}`
                              : undefined
                          }
                        >
                          {display}
                        </InstalledVersion>
                      </ComponentRow>
                    );
                  })
                )}
              </>
            ) : null}
          </ModalBody>
        </ModalBox>
      </Overlay>

      <OTAVehicleAssignModal
        isOpen={showAssign}
        vehicleId={vehicle.id}
        vehicleThingName={vehicle.awsThingName}
        vehicleVin={vehicle.vin || ""}
        fleetManagedByAssets={!!vehicle.legacyVehicleName}
        onClose={() => setShowAssign(false)}
        onSuccess={() => {
          setShowAssign(false);
          onDataChanged();
          onClose();
        }}
      />
    </>
  );
});

// ─── Component Delete Modal ───────────────────────────────────────────────────

const ProgressText = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 12px;
  min-height: 20px;
`;

const ComponentDeleteModal = memo<{
  component: OTAComponent;
  versions: OTAComponentVersion[];
  onClose: () => void;
  onDeleted: () => void;
}>(({ component, versions, onClose, onDeleted }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [progress, setProgress] = useState("");

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    try {
      for (let i = 0; i < versions.length; i++) {
        const v = versions[i];
        const hasRecipe = Boolean(v.awsComponentArn);
        setProgress(
          `Deleting version ${v.versionNumber} (${i + 1}/${versions.length})${
            hasRecipe ? " — removing from Greengrass…" : ""
          }`
        );
        await otaVersionsApi.delete(v.id);
      }
      setProgress("Deleting component…");
      await otaComponentsApi.delete(component.id);
      toast.success(`Component "${component.name}" and all versions deleted`);
      onDeleted();
    } catch {
      toast.error(
        "Deletion failed — some items may have been partially deleted"
      );
      setIsDeleting(false);
      setProgress("");
    }
  };

  return (
    <Overlay onClick={isDeleting ? undefined : onClose}>
      <ModalBox
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460, width: "100%" }}
      >
        <ModalHeader>
          <ModalTitleBlock>
            <ModalTitle>Delete Component: {component.name}</ModalTitle>
            <ModalSubtitle>This action cannot be undone</ModalSubtitle>
          </ModalTitleBlock>
          {!isDeleting && <CloseButton onClick={onClose}>×</CloseButton>}
        </ModalHeader>
        <ModalBody>
          {versions.length > 0 ? (
            <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.5 }}>
              This component has{" "}
              <strong>
                {versions.length} version{versions.length !== 1 ? "s" : ""}
              </strong>
              . Each version will be removed from AWS Greengrass (if a recipe
              exists) and then deleted from the database before the component is
              deleted.
            </p>
          ) : (
            <p style={{ fontSize: 14, color: "#374151" }}>
              This component has no versions. It will be deleted immediately.
            </p>
          )}
          {progress && <ProgressText>{progress}</ProgressText>}
        </ModalBody>
        <ModalFooter>
          <CancelBtn onClick={onClose} disabled={isDeleting}>
            Cancel
          </CancelBtn>
          <DeleteFleetBtn enabled={!isDeleting} onClick={handleDeleteAll}>
            {isDeleting
              ? "Deleting…"
              : versions.length > 0
              ? `Delete ${versions.length} version${
                  versions.length !== 1 ? "s" : ""
                } + component`
              : "Delete component"}
          </DeleteFleetBtn>
        </ModalFooter>
      </ModalBox>
    </Overlay>
  );
});

// ─── Component Popup ──────────────────────────────────────────────────────────

const ComponentPopup = memo<{
  component: OTAComponent;
  onClose: () => void;
  onDataChanged: () => void;
}>(({ component, onClose, onDataChanged }) => {
  const [versions, setVersions] = useState<OTAComponentVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddVersion, setShowAddVersion] = useState(false);
  const isDeprecated = component.isDeprecated;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteVersionTarget, setDeleteVersionTarget] =
    useState<OTAComponentVersion | null>(null);
  const [selectedVersionDetails, setSelectedVersionDetails] =
    useState<OTAComponentVersion | null>(null);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await otaVersionsApi.getByComponent(component.id);
      setVersions(data);
    } catch {
      toast.error("Failed to load versions");
    } finally {
      setLoading(false);
    }
  }, [component.id]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const handleDeleteVersion = async () => {
    if (!deleteVersionTarget) return;
    try {
      await otaVersionsApi.delete(deleteVersionTarget.id);
      setVersions((prev) =>
        prev.filter((item) => item.id !== deleteVersionTarget.id)
      );
      toast.success(`Version ${deleteVersionTarget.versionNumber} deleted`);
      setDeleteVersionTarget(null);
    } catch {
      toast.error("Failed to delete version");
    }
  };

  return (
    <>
      <Overlay onClick={onClose}>
        <ModalBox onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitleBlock>
              <ModalTitle>{component.name}</ModalTitle>
              <ModalSubtitle>
                {component.awsComponentName ?? "—"}
                {component.type ? ` · ${component.type}` : ""}
                {isDeprecated ? " · Deprecated" : ""}
              </ModalSubtitle>
            </ModalTitleBlock>
            <ModalHeaderActions>
              <AddVersionBtn onClick={() => setShowAddVersion(true)}>
                + Add Version
              </AddVersionBtn>
              <PopupActionBtn
                $variant="danger"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </PopupActionBtn>
            </ModalHeaderActions>
            <CloseButton onClick={onClose}>×</CloseButton>
          </ModalHeader>
          <ModalBody>
            {component.description && (
              <>
                <SectionLabel>Description</SectionLabel>
                <InfoItem style={{ marginBottom: 0 }}>
                  <InfoValue
                    style={{
                      fontWeight: 400,
                      fontSize: 13,
                      wordBreak: "normal",
                    }}
                  >
                    {component.description}
                  </InfoValue>
                </InfoItem>
              </>
            )}

            <SectionLabel>
              Versions ({loading ? "…" : versions.length})
            </SectionLabel>
            {loading ? (
              <LoadingText>Loading versions…</LoadingText>
            ) : versions.length === 0 ? (
              <EmptyBlock>No versions yet</EmptyBlock>
            ) : (
              versions.map((v) => (
                <VersionRow
                  key={v.id}
                  onClick={() => setSelectedVersionDetails(v)}
                  title="Click to view version details"
                >
                  <VersionNumber>{v.versionNumber}</VersionNumber>
                  <VersionMeta title={v.s3Uri ?? undefined}>
                    {v.s3Uri}
                  </VersionMeta>
                  {v.tags && v.tags.length > 0 && (
                    <span
                      style={{
                        display: "flex",
                        gap: 4,
                        flexWrap: "wrap",
                        flexShrink: 0,
                      }}
                    >
                      {v.tags.map((tag) => (
                        <span
                          key={tag}
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "1px 6px",
                            borderRadius: 4,
                            background: "#e0f2fe",
                            color: "#0369a1",
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </span>
                  )}
                  <span
                    style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}
                  >
                    {formatDate(v.createdAt)}
                  </span>
                  {v.isDeprecated && (
                    <DeprecatedBadge>Deprecated</DeprecatedBadge>
                  )}
                  <VersionActions onClick={(e) => e.stopPropagation()}>
                    <SmallActionBtn
                      $variant="danger"
                      onClick={() => setDeleteVersionTarget(v)}
                    >
                      Delete
                    </SmallActionBtn>
                  </VersionActions>
                </VersionRow>
              ))
            )}
          </ModalBody>
        </ModalBox>
      </Overlay>

      {showAddVersion && (
        <AddVersionModal
          component={component}
          onClose={() => setShowAddVersion(false)}
          onVersionCreated={() => {
            setShowAddVersion(false);
            loadVersions();
            onDataChanged();
          }}
        />
      )}

      {showDeleteModal && (
        <ComponentDeleteModal
          component={component}
          versions={versions}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => {
            onDataChanged();
            onClose();
          }}
        />
      )}

      <DeleteConfirmationModal
        isOpen={Boolean(deleteVersionTarget)}
        entityName={
          deleteVersionTarget
            ? `${component.name} v${deleteVersionTarget.versionNumber}`
            : ""
        }
        entityType="Component Version"
        onConfirm={handleDeleteVersion}
        onClose={() => setDeleteVersionTarget(null)}
      />

      {selectedVersionDetails && (
        <VersionDetailsModal
          version={selectedVersionDetails}
          componentType={component.type}
          onClose={() => setSelectedVersionDetails(null)}
          onVersionUpdated={(updated) => {
            setSelectedVersionDetails(updated);
            loadVersions();
          }}
        />
      )}
    </>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

export const OTAOverviewCards = memo(() => {
  const [fleets, setFleets] = useState<CardFleet[]>([]);
  const [vehicles, setVehicles] = useState<CardVehicle[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<CardVehicleType[]>([]);
  const [components, setComponents] = useState<OTAComponent[]>([]);
  const [loading, setLoading] = useState(true);
  const hasLoaded = useRef(false);

  const [selectedFleet, setSelectedFleet] = useState<CardFleet | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<CardVehicle | null>(
    null
  );
  const [selectedComponent, setSelectedComponent] =
    useState<OTAComponent | null>(null);

  const [showCreateFleet, setShowCreateFleet] = useState(false);
  const [showCreateVT, setShowCreateVT] = useState(false);
  const [showCreateComponent, setShowCreateComponent] = useState(false);
  const [componentTypes, setComponentTypes] = useState<string[]>([]);

  const [vehicleFleetFilter, setVehicleFleetFilter] = useState<string | null>(
    null
  );
  const [vehicleVTFilter, setVehicleVTFilter] = useState<string | null>(null);
  const fetchData = useCallback(async () => {
    if (!hasLoaded.current) setLoading(true);
    try {
      const env = getCurrentEnvironment();
      const vehiclesUrl = env
        ? `/ota/vehicles/?environment=${encodeURIComponent(env)}`
        : "/ota/vehicles/";
      const [fleetsRes, vehiclesRes, vtRes, componentsData] = await Promise.all(
        [
          API.get("/ota/fleets/"),
          API.get(vehiclesUrl),
          API.get("/ota/ota-vehicle-types/"),
          otaComponentsApi.getAll(),
        ]
      );
      otaComponentsApi
        .getTypes()
        .then(setComponentTypes)
        .catch((e) => log.error("getTypes failed", e));

      setFleets(
        Array.isArray(fleetsRes.data)
          ? fleetsRes.data
          : fleetsRes.data?.results ?? []
      );
      setVehicles(
        Array.isArray(vehiclesRes.data)
          ? vehiclesRes.data
          : vehiclesRes.data?.results ?? []
      );
      setVehicleTypes(
        Array.isArray(vtRes.data) ? vtRes.data : vtRes.data?.results ?? []
      );
      setComponents(componentsData);
      hasLoaded.current = true;
    } catch {
      toast.error("Failed to load overview data");
      if (!hasLoaded.current) {
        setFleets([]);
        setVehicles([]);
        setVehicleTypes([]);
        setComponents([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateVT = async (data: {
    name: string;
    description: string;
  }) => {
    await API.post("/ota/ota-vehicle-types/", data);
    toast.success(`Vehicle type "${data.name}" created`);
    setShowCreateVT(false);
    fetchData();
  };

  const handleCreateComponent = async (payload: OTAComponentCreatePayload) => {
    await otaComponentsApi.create(payload);
    toast.success(`Component "${payload.name}" created`);
    setShowCreateComponent(false);
    fetchData();
  };

  if (loading) return <LoadingText>Loading…</LoadingText>;

  const filteredVehicles = vehicles.filter((v) => {
    if (vehicleFleetFilter === "__unassigned__") {
      if (v.fleet) return false;
    } else if (vehicleFleetFilter && v.fleet !== vehicleFleetFilter) {
      return false;
    }
    if (vehicleVTFilter && v.vehicleType !== vehicleVTFilter) return false;
    return true;
  });

  const filteredComponents = components;

  const isVehicleFiltered = vehicleFleetFilter || vehicleVTFilter;

  return (
    <>
      {/* ── Fleets ─────────────────────────────────────────────────────────── */}
      <BlockWrapper>
        <BlockHeader>
          <BlockTitle>Fleets</BlockTitle>
          <BlockCount>
            {fleets.length} fleet{fleets.length !== 1 ? "s" : ""}
          </BlockCount>
          <CreateBtn onClick={() => setShowCreateFleet(true)}>
            + Create Fleet
          </CreateBtn>
        </BlockHeader>
        {fleets.length === 0 ? (
          <EmptyBlock>No fleets found</EmptyBlock>
        ) : (
          <CardsGrid>
            {fleets.map((fleet) => (
              <Card key={fleet.id} onClick={() => setSelectedFleet(fleet)}>
                <CardName>{fleet.name}</CardName>
                {fleet.description && <CardMeta>{fleet.description}</CardMeta>}
                <CardFooter>
                  <StatusDot $color={fleet.isActive ? "#22c55e" : "#9ca3af"} />
                  <span>{fleet.isActive ? "Active" : "Inactive"}</span>
                  <VehicleCountBadge>
                    {fleet.vehicleCount} vehicle
                    {fleet.vehicleCount !== 1 ? "s" : ""}
                  </VehicleCountBadge>
                </CardFooter>
              </Card>
            ))}
          </CardsGrid>
        )}
      </BlockWrapper>

      {/* ── Vehicles ───────────────────────────────────────────────────────── */}
      <BlockWrapper>
        <BlockHeader>
          <BlockTitle>Vehicles</BlockTitle>
          <BlockCount>
            {filteredVehicles.length}
            {isVehicleFiltered ? ` / ${vehicles.length}` : ""} vehicle
            {vehicles.length !== 1 ? "s" : ""}
          </BlockCount>
        </BlockHeader>
        {(fleets.length > 0 || vehicleTypes.length > 0) && (
          <FilterRow>
            {fleets.length > 0 && (
              <>
                <FilterLabel htmlFor="vehicle-fleet-filter">Fleet</FilterLabel>
                <FilterSelect
                  id="vehicle-fleet-filter"
                  value={vehicleFleetFilter ?? ""}
                  onChange={(e) =>
                    setVehicleFleetFilter(e.target.value || null)
                  }
                >
                  <option value="">All</option>
                  <option value="__unassigned__">Not assigned</option>
                  {fleets.map((fleet) => (
                    <option key={fleet.id} value={fleet.id}>
                      {fleet.name}
                    </option>
                  ))}
                </FilterSelect>
              </>
            )}
            {vehicleTypes.length > 0 && (
              <>
                <FilterLabel htmlFor="vehicle-vt-filter">
                  Vehicle Type
                </FilterLabel>
                <FilterSelect
                  id="vehicle-vt-filter"
                  value={vehicleVTFilter ?? ""}
                  onChange={(e) => setVehicleVTFilter(e.target.value || null)}
                >
                  <option value="">All</option>
                  {vehicleTypes.map((vt) => (
                    <option key={vt.id} value={vt.id}>
                      {vt.name}
                    </option>
                  ))}
                </FilterSelect>
              </>
            )}
          </FilterRow>
        )}
        {filteredVehicles.length === 0 ? (
          <EmptyBlock>No vehicles found</EmptyBlock>
        ) : (
          <CardsGrid>
            {filteredVehicles.map((vehicle) => (
              <Card
                key={vehicle.id}
                onClick={() => setSelectedVehicle(vehicle)}
              >
                <CardName>
                  {vehicle.awsThingName || vehicle.vin || (
                    <em style={{ color: "#9ca3af", fontStyle: "italic" }}>
                      No name
                    </em>
                  )}
                </CardName>
                <CardMeta
                  title={vehicle.id}
                  style={{ fontFamily: "monospace", fontSize: 11 }}
                >
                  {vehicle.id.slice(0, 8)}…
                </CardMeta>
                <CardMeta>
                  {vehicle.vehicleTypeName || (
                    <em style={{ color: "#9ca3af" }}>No type</em>
                  )}
                </CardMeta>
                {vehicle.fleetName && <CardMeta>{vehicle.fleetName}</CardMeta>}
                <CardFooter>
                  <StatusDot $color={statusColor(vehicle.connectionStatus)} />
                  <span style={{ textTransform: "capitalize" }}>
                    {vehicle.connectionStatus}
                  </span>
                </CardFooter>
              </Card>
            ))}
          </CardsGrid>
        )}
      </BlockWrapper>

      {/* ── Vehicle Types ──────────────────────────────────────────────────── */}
      <BlockWrapper>
        <BlockHeader>
          <BlockTitle>Vehicle Types</BlockTitle>
          <BlockCount>
            {vehicleTypes.length} type{vehicleTypes.length !== 1 ? "s" : ""}
          </BlockCount>
          <CreateBtn onClick={() => setShowCreateVT(true)}>
            + Create Vehicle Type
          </CreateBtn>
        </BlockHeader>
        {vehicleTypes.length === 0 ? (
          <EmptyBlock>No vehicle types found</EmptyBlock>
        ) : (
          <CardsGrid>
            {vehicleTypes.map((vt) => (
              <Card key={vt.id}>
                <CardName>{vt.name}</CardName>
                {vt.description && <CardMeta>{vt.description}</CardMeta>}
                {vt.isDeprecated && (
                  <CardFooter>
                    <DeprecatedBadge style={{ marginLeft: "auto" }}>
                      Deprecated
                    </DeprecatedBadge>
                  </CardFooter>
                )}
              </Card>
            ))}
          </CardsGrid>
        )}
      </BlockWrapper>

      {/* ── Components ─────────────────────────────────────────────────────── */}
      <BlockWrapper>
        <BlockHeader>
          <BlockTitle>Components</BlockTitle>
          <BlockCount>
            {filteredComponents.length} component
            {components.length !== 1 ? "s" : ""}
          </BlockCount>
          <CreateBtn onClick={() => setShowCreateComponent(true)}>
            + Create Component
          </CreateBtn>
        </BlockHeader>
        {components.length === 0 ? (
          <EmptyBlock>No components found</EmptyBlock>
        ) : (
          <CardsGrid>
            {filteredComponents.map((comp) => (
              <Card key={comp.id} onClick={() => setSelectedComponent(comp)}>
                <CardName>{comp.name}</CardName>
                {comp.type && <CardMeta>{comp.type}</CardMeta>}
                {comp.awsComponentName && (
                  <CardMeta title={comp.awsComponentName}>
                    {comp.awsComponentName}
                  </CardMeta>
                )}
                <CardFooter>
                  <span style={{ color: "#94a3b8", fontSize: 12 }}>
                    Click to view versions
                  </span>
                  {comp.isDeprecated && (
                    <DeprecatedBadge style={{ marginLeft: "auto" }}>
                      Deprecated
                    </DeprecatedBadge>
                  )}
                </CardFooter>
              </Card>
            ))}
          </CardsGrid>
        )}
      </BlockWrapper>

      {/* ── Popups ─────────────────────────────────────────────────────────── */}
      {selectedFleet && (
        <FleetModal
          fleet={selectedFleet}
          vehicles={vehicles}
          onClose={() => setSelectedFleet(null)}
        />
      )}
      {selectedVehicle && (
        <VehicleModal
          vehicle={selectedVehicle}
          onClose={() => setSelectedVehicle(null)}
          onDataChanged={fetchData}
        />
      )}
      {selectedComponent && (
        <ComponentPopup
          component={selectedComponent}
          onClose={() => setSelectedComponent(null)}
          onDataChanged={fetchData}
        />
      )}

      {/* ── Create Modals ───────────────────────────────────────────────────── */}
      <CreateFleetModal
        isOpen={showCreateFleet}
        onClose={() => setShowCreateFleet(false)}
        onSuccess={() => {
          setShowCreateFleet(false);
          fetchData();
        }}
      />

      {showCreateVT && (
        <AddVehicleTypeModal
          onClose={() => setShowCreateVT(false)}
          onSubmit={handleCreateVT}
        />
      )}
      <AddComponentModal
        isOpen={showCreateComponent}
        onClose={() => setShowCreateComponent(false)}
        onSubmit={handleCreateComponent}
        componentTypes={componentTypes}
      />
    </>
  );
});
